import { useState, useRef, useMemo } from 'react'
import { Search, Upload, FileText, Loader2, X, Download, Share2, CheckSquare, Square, Trash2 } from 'lucide-react'
import { useLibrary } from '@/hooks/useApi'
import { api } from '@/lib/api'
import { useQueryClient } from '@tanstack/react-query'

const LONG_PRESS_MS = 500

interface LibraryItem {
  item_id: string
  filename: string
  mime_type: string
  size: number
  is_image: boolean
  uploaded_at: string
  serve_url: string
}

function fixServeUrl(url: string): string {
  return url.replace(/https?:\/\/ops-bridge[^/]*\.workers\.dev/, '/api')
}

function fmtSize(bytes: number): string {
  if (bytes < 1024) return bytes + ' B'
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB'
  return (bytes / (1024 * 1024)).toFixed(1) + ' MB'
}

function fmtDate(iso: string): string {
  return new Date(iso).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
}

function FileCell({ item, onClick, selectMode, selected, onToggleSelect, onLongPress }: { item: LibraryItem; onClick: () => void; selectMode: boolean; selected: boolean; onToggleSelect: () => void; onLongPress: () => void }) {
  const url = fixServeUrl(item.serve_url)
  const timerRef = useRef<number | null>(null)
  const firedRef = useRef(false)

  function startPress() {
    firedRef.current = false
    timerRef.current = window.setTimeout(() => {
      firedRef.current = true
      onLongPress()
    }, LONG_PRESS_MS)
  }
  function clearPress() {
    if (timerRef.current) { clearTimeout(timerRef.current); timerRef.current = null }
  }
  function endPress() {
    clearPress()
    if (!firedRef.current) { selectMode ? onToggleSelect() : onClick() }
  }

  return (
    <div
      onPointerDown={startPress}
      onPointerUp={endPress}
      onPointerLeave={clearPress}
      onPointerCancel={clearPress}
      onContextMenu={e => e.preventDefault()}
      style={{ cursor: 'pointer', borderRadius: 12, overflow: 'hidden', background: '#0d1526', border: selected ? '2px solid #0066ff' : '1px solid rgba(255,255,255,0.06)', position: 'relative', userSelect: 'none', WebkitUserSelect: 'none', touchAction: 'manipulation' }}
    >
      {selectMode && (
        <div style={{ position: 'absolute', top: 8, left: 8, zIndex: 2, background: 'rgba(0,0,0,0.55)', borderRadius: 6, padding: 3, display: 'flex' }}>
          {selected ? <CheckSquare size={16} color="#0066ff" /> : <Square size={16} color="#fff" />}
        </div>
      )}
      {item.is_image ? (
        <img
          src={url}
          alt={item.filename}
          loading="lazy"
          draggable={false}
          style={{ width: '100%', aspectRatio: '1/1', objectFit: 'cover', display: 'block' }}
        />
      ) : (
        <div style={{ width: '100%', aspectRatio: '1/1', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 8, padding: 12 }}>
          <FileText size={32} color="#6b7a99" />
          <span style={{ fontSize: 11, color: '#94a3b8', textAlign: 'center', wordBreak: 'break-word', lineHeight: 1.4 }}>
            {item.filename.length > 24 ? item.filename.slice(0, 21) + '...' : item.filename}
          </span>
        </div>
      )}
    </div>
  )
}

function ItemViewer({ item, onClose }: { item: LibraryItem; onClose: () => void }) {
  const url = fixServeUrl(item.serve_url)
  return (
    <div
      style={{ position: 'fixed', inset: 0, zIndex: 9999, background: 'rgba(0,0,0,0.94)', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}
      onClick={e => { if (e.target === e.currentTarget) onClose() }}
    >
      <div style={{ position: 'absolute', top: 16, right: 20, display: 'flex', gap: 8 }}>
        <a href={url} download={item.filename} style={{ textDecoration: 'none' }}>
          <button style={btnStyle}><Download size={13} />Download</button>
        </a>
        <button
          onClick={async () => {
            if (navigator.share) { try { await navigator.share({ title: item.filename, url }); return } catch { /* fall through */ } }
            navigator.clipboard.writeText(url).catch(() => {})
          }}
          style={btnStyle}
        >
          <Share2 size={13} />Share
        </button>
        <button onClick={onClose} style={{ ...btnStyle, fontSize: 16, padding: '4px 10px' }}>
          <X size={14} />
        </button>
      </div>
      {item.is_image ? (
        <img src={url} alt={item.filename} style={{ maxWidth: '90vw', maxHeight: '82vh', objectFit: 'contain', borderRadius: 10 }} />
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 16, color: '#94a3b8' }}>
          <FileText size={64} />
          <span style={{ fontSize: 15 }}>{item.filename}</span>
          <span style={{ fontSize: 12, opacity: 0.6 }}>Preview not available for this file type -- download to view.</span>
        </div>
      )}
      <div style={{ marginTop: 14, fontSize: 12, color: '#6b7a99' }}>{item.filename} &middot; {fmtSize(item.size)} &middot; {fmtDate(item.uploaded_at)}</div>
    </div>
  )
}

const btnStyle = { background: 'rgba(255,255,255,0.07)', border: '1px solid rgba(255,255,255,0.12)', color: '#e2e8f0', borderRadius: 8, padding: '5px 12px', fontSize: 11, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 5 } as const

export function LibraryPanel() {
  const { data, isLoading } = useLibrary()
  const queryClient = useQueryClient()
  const items: LibraryItem[] = data?.items ?? []

  const [tab, setTab] = useState<'all' | 'images' | 'files'>('all')
  const [search, setSearch] = useState('')
  const [viewing, setViewing] = useState<LibraryItem | null>(null)
  const [uploading, setUploading] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const [selectMode, setSelectMode] = useState(false)
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set())
  const [deleting, setDeleting] = useState(false)

  function toggleSelect(id: string) {
    setSelectedIds(prev => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  function enterSelectMode(id: string) {
    setSelectMode(true)
    setSelectedIds(new Set([id]))
  }

  function exitSelectMode() {
    setSelectMode(false)
    setSelectedIds(new Set())
  }

  async function handleDeleteSelected() {
    if (selectedIds.size === 0) return
    if (!confirm(`Delete ${selectedIds.size} item${selectedIds.size !== 1 ? 's' : ''}? This can't be undone.`)) return
    setDeleting(true)
    try {
      await api.libraryDelete(Array.from(selectedIds))
      queryClient.invalidateQueries({ queryKey: ['dashboard', 'library'] })
      exitSelectMode()
    } catch {
      alert('Delete failed -- please try again.')
    } finally {
      setDeleting(false)
    }
  }

  const filtered = useMemo(() => {
    let list = items
    if (tab === 'images') list = list.filter(i => i.is_image)
    if (tab === 'files') list = list.filter(i => !i.is_image)
    if (search.trim()) {
      const q = search.trim().toLowerCase()
      list = list.filter(i => i.filename.toLowerCase().includes(q))
    }
    return list
  }, [items, tab, search])

  async function handleUpload(e: React.ChangeEvent<HTMLInputElement>) {
    const files = e.target.files
    if (!files || files.length === 0) return
    setUploading(true)
    try {
      for (const file of Array.from(files)) {
        const data = await new Promise<string>((resolve, reject) => {
          const reader = new FileReader()
          reader.onload = () => resolve((reader.result as string).split(',')[1] ?? '')
          reader.onerror = reject
          reader.readAsDataURL(file)
        })
        await api.libraryUpload(file.name, file.type || 'application/octet-stream', data)
      }
      queryClient.invalidateQueries({ queryKey: ['dashboard', 'library'] })
    } catch {
      alert('Upload failed -- please try again.')
    } finally {
      setUploading(false)
      if (fileInputRef.current) fileInputRef.current.value = ''
    }
  }

  const TABS: Array<{ key: typeof tab; label: string }> = [
    { key: 'all', label: 'All' },
    { key: 'images', label: 'Images' },
    { key: 'files', label: 'Files' },
  ]

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
        <div>
          <h2 style={{ fontSize: 20, fontWeight: 800, color: 'var(--color-text-primary)', margin: '0 0 4px 0' }}>Library</h2>
          <p style={{ fontSize: 12, color: 'var(--color-text-muted)', margin: 0 }}>
            {isLoading ? 'Loading...' : `${items.length} item${items.length !== 1 ? 's' : ''}`} &middot; everything you upload, not AI-generated &middot; press and hold an item to select
          </p>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          {selectMode ? (
            <>
              <button
                onClick={() => {
                  if (selectedIds.size === filtered.length) setSelectedIds(new Set())
                  else setSelectedIds(new Set(filtered.map(i => i.item_id)))
                }}
                style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '9px 16px', borderRadius: 10, background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.12)', color: '#e2e8f0', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}
              >
                {selectedIds.size === filtered.length && filtered.length > 0 ? 'Deselect all' : 'Select all'}
              </button>
              <button
                onClick={handleDeleteSelected}
                disabled={selectedIds.size === 0 || deleting}
                style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '9px 16px', borderRadius: 10, background: selectedIds.size === 0 ? 'rgba(239,68,68,0.25)' : '#ef4444', border: 'none', color: '#fff', fontSize: 13, fontWeight: 600, cursor: selectedIds.size === 0 || deleting ? 'default' : 'pointer', opacity: deleting ? 0.6 : 1 }}
              >
                {deleting ? <Loader2 size={14} className="animate-spin" /> : <Trash2 size={14} />}
                {deleting ? 'Deleting...' : `Delete (${selectedIds.size})`}
              </button>
              <button
                onClick={exitSelectMode}
                style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '9px 16px', borderRadius: 10, background: 'transparent', border: '1px solid rgba(255,255,255,0.12)', color: '#94a3b8', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}
              >
                Cancel
              </button>
            </>
          ) : (
            <button
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
              style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '9px 16px', borderRadius: 10, background: 'linear-gradient(135deg,#0066ff,#0055ee)', border: 'none', color: '#fff', fontSize: 13, fontWeight: 600, cursor: uploading ? 'default' : 'pointer', opacity: uploading ? 0.6 : 1 }}
            >
              {uploading ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />}
              {uploading ? 'Uploading...' : 'Upload'}
            </button>
          )}
        </div>
        <input ref={fileInputRef} type="file" multiple onChange={handleUpload} style={{ display: 'none' }} />
      </div>

      <div style={{ display: 'flex', gap: 4, marginBottom: 16, borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
        {TABS.map(t => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            style={{
              padding: '8px 4px', marginRight: 20, background: 'transparent', border: 'none', cursor: 'pointer',
              fontSize: 13, fontWeight: 600,
              color: tab === t.key ? 'var(--color-text-primary)' : 'var(--color-text-muted)',
              borderBottom: tab === t.key ? '2px solid #0066ff' : '2px solid transparent',
            }}
          >
            {t.label}
          </button>
        ))}
      </div>

      {isLoading && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: '#6b7a99', fontSize: 12, padding: '40px 0' }}>
          <Loader2 size={16} className="animate-spin" />Loading library...
        </div>
      )}

      {!isLoading && filtered.length === 0 && (
        <div style={{ textAlign: 'center', padding: '60px 0', color: '#6b7a99' }}>
          <FileText size={40} style={{ opacity: 0.2, margin: '0 auto 12px' }} />
          <p style={{ fontSize: 13 }}>{items.length === 0 ? 'Nothing uploaded yet' : 'No matches'}</p>
          {items.length === 0 && <p style={{ fontSize: 11, opacity: 0.7, marginTop: 4 }}>Use Upload above to add photos, scans, or documents</p>}
        </div>
      )}

      {/* 3-column grid, per direct instruction */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10 }}>
        {filtered.map(item => (
          <FileCell
            key={item.item_id}
            item={item}
            onClick={() => setViewing(item)}
            selectMode={selectMode}
            selected={selectedIds.has(item.item_id)}
            onToggleSelect={() => toggleSelect(item.item_id)}
            onLongPress={() => enterSelectMode(item.item_id)}
          />
        ))}
      </div>

      {/* Search -- positioned at the bottom, matching the reference layout */}
      <div style={{ position: 'sticky', bottom: 16, marginTop: 24, display: 'flex', justifyContent: 'center' }}>
        <div style={{ position: 'relative', width: '100%', maxWidth: 480 }}>
          <Search size={15} style={{ position: 'absolute', left: 14, top: '50%', transform: 'translateY(-50%)', color: '#6b7a99', pointerEvents: 'none' }} />
          <input
            type="text"
            placeholder="Search library"
            value={search}
            onChange={e => setSearch(e.target.value)}
            style={{ width: '100%', background: '#0d1526', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 999, padding: '11px 16px 11px 40px', fontSize: 13, color: '#e2e8f0', boxSizing: 'border-box' }}
          />
        </div>
      </div>

      {viewing && <ItemViewer item={viewing} onClose={() => setViewing(null)} />}
    </div>
  )
}
